/* eslint-disable max-len */
import { projectDomainName, projectStyledName } from 'shared/constants'

export interface ITermCondition {
  label: string
  items: { text?: string; link?: string; bottom?: string; subItems?: string[] }[]
}

export const termsConditions: Record<string, ITermCondition> = {
  generalTerms: {
    label: 'General Terms',
    items: [
      {
        text: `1.1. These Terms and Conditions (“T&C”), in the valid version, apply to the use of the
      betting offer (as defined below) by the customer through the website
      (${projectDomainName}) and other URLs belonging or licensed to ${projectStyledName}, including the
      related Internet, mobile or other platforms.`,
      },
      {
        text: `1.2. These T&C constitute a binding agreement between the customer and ${projectStyledName}.`,
      },
      {
        text: `1.3. These T&C apply once the customer uses ${projectStyledName}. This means that the
        customer places a bet in a shop. By placing a bet, the customer agrees to these
        T&C.`,
      },
      {
        text: `1.4. The customer is obligated to read these T&C carefully and entirely before he
        starts using ${projectStyledName} products. If the customer does not agree with some of the
        provisions of these T&C, he should not continue to use the services provided by
        the website or shop.`,
      },
      {
        text: `1.5. By placing a bet, the customer as a user concludes a betting contract with
        ${projectStyledName}, the betting organizer.`,
      },
      {
        text: `1.6. ${projectStyledName} reserves the right to modify these T&C.`,
      },
      {
        text: `1.7. The customer declares to have fully understood the currently valid version of
        T&C and agrees to comply with them.`,
      },
      {
        text: `1.8. ${projectStyledName} reserves the right to modify and amend this agreement at any time.`,
      },
      {
        text: `1.9. ${projectStyledName} reserves the right to modify and amend this agreement at any time.`,
      },
      {
        text: `1.10. These amendments will become effective immediately after being posted on
        the Internet. It is the customer’s sole responsibility to review this agreement and
        amendments before placing a bet.`,
      },
      {
        text: `1.11. Rules and explanations for placing various types of bets on various events
        provided separately on the website are incorporated into these T&C.`,
      },
      {
        text: `1.12. These T&C are published in several languages for information purposes and
        ease of access by the customer. However, only the English version serves as a
        legal basis for the business relationship between ${projectStyledName} and the customer. In
        case of any discrepancy between the English version of the T&C and versions in
        other languages, the English version will prevail.`,
      },
    ],
  },
  eligibilityUse: {
    label: `Eligibility to use ${projectStyledName} Services`,
    items: [
      {
        text: `2.1. The customer is entitled to use ${projectStyledName} services if:`,
        subItems: [
          `2.1.1. He is over eighteen (18+) years old or of the corresponding legal age as
          stipulated by the jurisdiction of his residence;`,
          `2.1.2. It is legal for the customer to participate in sports betting in accordance with
          the applicable laws in the jurisdiction of his residence;`,
          `2.1.3. The customer does not place bets on behalf of bookmakers or betting agents
          and their employees.`,
          `2.1.4. The customer is not involved in a sporting event (e.g. as a participating
          athlete, owner, trainer or an official of a participating club).`,
        ],
      },
      {
        text: `2.2. ${projectStyledName} reserves the right to cancel bets that are placed in breach of these
        conditions, even after the event has finished.`,
      },
      {
        text: `2.3. It is entirely and solely the responsibility of the customer to obtain information
        and ensure not to breach applicable laws when participating in a bet.`,
      },
    ],
  },
  calculationWinning: {
    label: 'Winnings and Calculation of Winnings',
    items: [
      {
        text: `3.1. Winnings on bets with fixed odds are calculated by multiplying the odds with
        the stake.`,
      },
      {
        text: `3.2. All markets will be decided and settled as soon as the outcome is known.
        ${projectStyledName} only accepts the results that occur during the event. Any changes that are
        made after this time have no influence on the evaluation of the betting event. Bets
        that have already been decided at the time of the interruption (live bets,
        half-time bets, match period bets, over/under bets, first goal etc.) are settled
        without regard to the time of the interruption or resumption of the match. If the
        event is canceled after the regular time (extra time / penalty shoot-out), the
        result after the regular time applies.`,
      },
      {
        text: `3.3. If a sporting event that was canceled or interrupted is restarted or continued
        within 48 hours of the advertised start time, the bet will apply to the rearranged
        fixture. If the match is not restarted or continued, it will be declared invalid. This
        does not apply to already determined markets (e.g. first goal, next goal, half-time
        bets, over/under bets). League matches or matches played within a tournament
        (e.g. Cup matches) ended by the referee before completion of the regular time of
        the match, will be settled according to the final score at that time if the referee
        declares the match finished with the final whistle and if the match is added to the
        official tables and is not replayed or continued at a later date.`,
      },
      {
        text: `3.4. For all sports bets it is the case that if the player wagered on does not start,
        regardless of the reason, but the event itself starts, the bet has been lost. However,
        if the entire betting event does not take place, the bet will be declared canceled.
        For the bet type “goal scorer” applies that the player wagered on must participate
        in the match at the start of the event, otherwise the bet will be canceled.
        Subsequent substitution will not be taken into account.`,
      },
      {
        text: `3.5. Dead heat: If two or more participants in a sporting event are declared
        winners, the betting odds will be divided accordingly.`,
      },
      {
        text: `3.6. ${projectStyledName} offers, under some circumstances, a so-called “Cashout'' for certain
        betting slips. The customer cannot request a Cashout himself; this offer is optional
        and depends on ${projectStyledName}.`,
      },
      {
        text: `3.7. Any attempt to participate, directly or indirectly, in collusive arrangements
        with regard to any betting activity is strictly prohibited.`,
      },
      {
        text: `3.8. All transactions are checked for possible money laundering. Any
        account-suspicious activity will be reported to the authorities and the balance of
        the betting account will be frozen. Closing the account and confiscation of the
        funds is also possible.`,
      },
      {
        text: `3.9. In all the aforementioned cases, ${projectStyledName} will contact the customer
        immediately (by email or other) and ask for feedback. If the customer fails to
        submit a feedback within the given period, ${projectStyledName} is entitled to terminate the
        customer's account until the situation and the resulting legal consequences are
        clarified.`,
      },
      {
        bottom: `${projectStyledName} reserves the right to assert claims for damages against the customer, if he
        was involved in the incurred damage.`,
      },
    ],
  },
  userAccount: {
    label: 'User Account',
    items: [
      {
        text: `4.1. Opening a user account must be carried out personally by the customer or
        official representative of ${projectStyledName}.`,
        subItems: [
          `4.1.1. In order to be able to place bets, the customer needs to open an account
          with ${projectStyledName} first.`,
          `4.1.2. The registration is done either through the website or at a betting shop. The
          provisions of the registration are noted in the instructions.`,
          `4.1.3. The customer must provide all information requested (first name, last name,
          address, contact, valid email address and payment information). It is the sole
          responsibility of the customer to ensure that the information he has provided is
          true, complete and correct.`,
          `${projectStyledName} is not liable for the misuse of betting accounts. The customer is
          responsible to maintain the confidentiality of his username and password and to
          assume responsibility for all activities that occur under his username and
          password.
          `,
          `4.1.4. The customer is hereby notified that ${projectStyledName} carries out verification
          procedures of user accounts. A user account may be blocked or closed if it is
          found out that the customer has provided incorrect or misleading information.`,
          `4.1.5. The customer is not entitled to more than one user account. If the customer
          tries to open more than one account, all duplicate accounts will be closed. ${projectStyledName}
          in this case reserves the right to deny the customer access to his account.`,
          `4.1.6. If the customer notices that he has more than one user account under
          different names, he must notify ${projectStyledName} immediately.`,
          `4.1.7. As part of the registration process, the customer will be asked to choose a
          username and password to log in to the website. It is the sole and exclusive
          responsibility of the customer to ensure that the login details are kept securely. He
          must not disclose his login details to anyone. ${projectStyledName} is not responsible for any
          abuse or misuse of a user account by third parties due to disclosure of the login
          details by the customer, whether intentional or accidental, whether active or
          passive.`,
          `4.1.8. ${projectStyledName} reserves the right to deny registration without stating the reasons.
          The customer can close his user account at any time. In that respect he can
          contact our customer service. In the event of closing an account at the
          customer’s request, ${projectStyledName} shall pay out the existing balance to the customer.`,
        ],
      },
      {
        text: '4.2. Deposits into User Account',
        subItems: [
          '4.2.1. The customer can place bets only if there are sufficient funds in his account.',
          `${projectStyledName} does not offer any credit whatsoever for participation in sports betting and casino betting.`,
          `4.2.2. The minimum deposit is ARS 10; the maximum deposit is ARS 10,000. There are no charges nor
          processing fees for the customer.`,
          `4.2.3. To deposit funds into his account, the customer can refill from any representative or shops
          legit of ${projectStyledName}.`,
          `4.2.4. ${projectStyledName} reserves the right to use additional procedures and means to verify the identity of the
          customer when the customer account is assumed to be compromised. The customer is not entitled to receive
          interest or other sums produced by the account. The account balance is displayed in the top right corner
          of the website.`,
        ],
      },
      {
        text: '4.3. Withdrawals from User Account',
        subItems: [
          `4.3.1. Withdrawals are made into his account, the customer can withdraw from any representative or shops
          legit of ${projectStyledName}.`,
          `4.3.2. ${projectStyledName} will carry out additional verification procedures for any withdrawal exceeding the amount of
          ARS 3,000 and reserves the right to carry such verification procedures in case of lower payouts.`,
          '4.3.3. The customer can make withdrawals of a minimum of ARS 10,00.',
          '4.3.4. The maximum profit paid to the client does not exceed ARS 25,000.',
          '4.3.5. The maximum balance of the client does not exceed ARS 25,000.',
        ],
      },
      {
        text: '4.4. Inactive User Accounts',
        subItems: [
          `4.4.1. If there is no activity on an account for 24 months, the account is considered inactive. ${projectStyledName}
          will transfer the account’s balance to the bank account provided by the customer. The user account will
          be blocked.`,
        ],
      },
    ],
  },
  customerObligations: {
    label: 'The Customer’s obligations',
    items: [
      {
        text: '5.1. The customer hereby declares and warrants that',
        subItems: [
          `5.1.1. He is over 18 years old or of minimum legal age as stipulated by the jurisdiction of his
          residence and legally allowed to use the services offered by ${projectStyledName}.`,
          `5.1.2. He is participating in sports betting and casino betting for recreational and entertainment
          reasons only.`,
          `5.1.3. He is participating in sports betting on his own behalf and not on the behalf of any other
          person.`,
          `5.1.4. All information provided to ${projectStyledName} during this agreement is true, complete, and correct,
          and that any changes will be communicated.`,
          `5.1.5. He is solely responsible for the reporting and accounting of any taxes applicable under
          relevant laws for any winnings received from ${projectStyledName}.`,
          `5.1.6. Money that the customer deposits into his account does not originate from any illegal
          activity or source.`,
          '5.1.7. He understands the risk of losing money when participating in sports betting and casino betting.',
          `5.1.8. He shall not be involved in any fraudulent, collusive, fixing or other unlawful activity in relation
          to his or third parties’ participation in any of the sports bets and shall not use any software-assisted
          methods or techniques or hardware devices for this purpose. ${projectStyledName} hereby reserves the right to invalidate
          any betting in the event of such behavior.`,
        ],
      },
      {
        text: '5.2. It is prohibited to the customer to sell, transfer and/or acquire funds to/from other players.',
      },
      {
        text: `5.3. The computer software that we make available to the customer is owned by ${projectStyledName} (e.g. App) and
        protected by copyright laws. The customer may only use the software for his own personal, recreational
        uses in accordance with all rules, terms and conditions we have established and in accordance with all
        applicable laws, rules and regulations. The customer hereby agrees to abide by the terms of use as attached
        to the End User License Agreement.`,
      },
      {
        text: '5.4.  Bets placed on our website should be placed in the same manner as played in any other setting.',
      },
      {
        bottom: 'This means that customers should be courteous to each other and avoid rude or obscene comments.',
      },
      {
        text: `5.5. The customer is strictly prohibited from utilizing ${projectStyledName} and its systems to facilitate any type of
        illegal money transfer system. The customer will not use the website for any unlawful or fraudulent activity
        or prohibited transaction (including money laundering) under the laws of any jurisdiction that applies to him.
        If ${projectStyledName} has suspicion that the customer may be engaging in or have engaged in fraudulent, unlawful or
        improper activity, including, without limitation, money laundering activities, or conduct otherwise in
        violation of these T&C, his access to the website will be terminated immediately and/or his account blocked.
        If the customer’s account is terminated or blocked in such circumstances, ${projectStyledName} is under no obligation to
        refund his account balance. Furthermore, ${projectStyledName} shall be entitled to inform relevant authorities, other online
        service providers and banks, credit card companies, electronic payment providers or other financial
        institutions of the customer’s identity and of any suspected unlawful, fraudulent or improper activity,
        and the customer will cooperate fully with ${projectStyledName} to investigate any such activity.`,
      },
    ],
  },
  responsibleGaming: {
    label: 'Responsible Gaming',
    items: [
      {
        text: `6.1. The customer may at his discretion choose to exclude himself from placing any bets on our website.
        In order to prevent access to our services, he needs to contact a representative of ${projectStyledName} who has opened
        this account.`,
      },
      {
        text: `6.2. When setting up a user account, the customer may also choose to impose a limit on the maximum
        stake he may wager. If the maximum stake is exceeded at any time, the user account will automatically be
        blocked.`,
      },
      {
        text: `6.3. Should the customer decide for self-exclusion in the manners contemplated above, then he will not
        be able to reverse this position for at least 7 days, after which he will be able to request a reactivation.`,
      },
      {
        text: `6.4. A customer who has set a limit as above in (6.1) and (6.2) may change or revoke the limit in the
        user account settings. Increasing or revoking a limit takes effect 7 days after ${projectStyledName} has registered the
        change. Decreasing a limit becomes effective immediately after ${projectStyledName} has registered the change. ${projectStyledName} will
        not accept a wager that is to the limit or exclusion set by the account holder as above.`,
      },
      {
        text: `6.5. ${projectStyledName} suggests that customers who have difficulties following the above basic rules
        (even in individual points) should seek professional help. There are lots of independent organizations that
        offer help to compulsive gamblers. Further information regarding this can be found at`,
      },
      {
        link: 'http://www.gamblingtherapy.org.uk/',
      },
    ],
  },
  casinoBets: {
    label: 'Casino Bets',
    items: [
      {
        text: '7.1. A bet can only be placed by a registered customer.',
      },
      {
        text: '7.2. A bet can only be placed via the website',
      },
      {
        text: '7.3. A bet can only be placed by a customer who has sufficient funds in his account.',
      },
      {
        text: '7.4. The product of bets',
        subItems: [
          `7.4.1. By accepting a Bet, ${projectStyledName} withholds the User's funds in the amount of the accepted Bet.`,
          '7.4.2. The minimum Bet amount is 0.01 ARS.',
          `7.4.3. The maximum Bet amount, depending on the type of Event, can be from ARS  0.01 to ARS 3,000
          or an equivalent amount in another currency.`,
          '7.4.4. A bet is accepted only for an Event that is on the Gambling List.',
          `7.4.5. By placing a Bet, the User assures that he has read and agrees with the status set for the
          participants of the Event.`,
          `7.4.6. The moment of acceptance of the Bet is registered in the ${projectStyledName} database. In the event of a dispute,
          this moment is considered indisputable and final.`,
          `7.4.7. ${projectStyledName} may accept, not accept or partially accept Bets without explanation.`,
          `7.4.8. The Bet is accepted from the moment it is registered in the ${projectStyledName} database.`,
          '7.4.9. The User cannot cancel the accepted Bets.',
          `7.4.10. Only the outcome of the Event expressed by ${projectStyledName} is the basis for recognizing the
          Bet as correctly or incorrectly Predicted.`,
          '7.4.11. The winning amount can be changed according to the points (4.3.4) and (4.3.4).',
        ],
      },
    ],
  },
  sportsBets: {
    label: 'Sports Bets',
    items: [
      {
        text: '8.1. A bet can only be placed by a registered customer.',
      },
      {
        text: '8.2. A bet can only be placed via the website',
      },
      {
        text: '8.3. A bet can only be placed by a customer who has sufficient funds in his account.',
      },
      {
        text: `8.4. The bet, once concluded, will be subject to the version of Terms and Conditions valid at the time
        of the bet being accepted.`,
      },
      {
        text: `8.5. A bet has been placed as soon as the customer receives a confirmation of acceptance for a betting
        offer from ${projectStyledName}. When several bets are placed by a customer, the bets will be processed in the order they
        are received by ${projectStyledName}.`,
      },
      {
        text: `8.6. The customer agrees that at the time he placed a bet(s), he had no knowledge as to the outcome of
        the respective event(s). A customer is not allowed to bet on events where he is directly involved or has
        access to inside information. Where there is suspicion of a violation of this rule, ${projectStyledName} retains the
        right to void the bet and to refuse the winnings. ${projectStyledName} also retains the right to take any further action
        to protect its legitimate interests and to comply with local and international laws and regulations. Any
        suspicious activities may also be submitted to various monitoring agencies that are set up to protect and
        uphold the integrity of sports.`,
      },
      {
        text: `8.7. ${projectStyledName} retains the right to decline any bet without providing any reason.`,
      },
      {
        text: '8.8. The minimum stake per bet is ARS 0.1.',
      },
      {
        text: `8.9. The list of all bets, their status and further details is available to the customer on the
        website.`,
      },
      {
        text: `8.10. ${projectStyledName} retains the right to declare all bets on an event/s invalid if they emanate from a group of
        users who appear to have had knowledge of the outcome of such event/s, e.g. manipulated sporting events.`,
      },
      {
        text: `8.11. Any individual or group deemed to be working as a team who manipulate promotions or any other
        offer will have their account closed immediately. Moreover, the transactions (bets) will not be settled as
        bets and are therefore not relevant.`,
      },
      {
        text: `8.12. When a bet is declared invalid or void, it will be calculated at odds of 1.00. The stake will be
        refunded to the customer. In the case of combination bets, the invalid result will not be included in the
        calculation of the (total) odds.`,
      },
      {
        text: `8.13. Any bet placed after the start of an event is invalid, and such bet will be calculated at odds
        of 1.00. The only exception to this rule are bets placed on Live Betting events (i.e. in-running betting).`,
      },
      {
        text: `8.14. Should ${projectStyledName} decide to close a user’s account for any reason, bets that have already been placed
        and accepted under the general terms and conditions and sports rules will not be voided and the user
        will be paid any winnings. Should security questions arise in relation to an account, ${projectStyledName} reserves
        the right to void such transactions (bets) within the legal and regulatory scope.`,
      },
      {
        text: `8.15. ${projectStyledName} manages users’ accounts and calculates available funds, pending funds, betting funds, as
        well as the amount of the winnings. Unless proven otherwise, these amounts are considered as final and are not
        subject to dispute.`,
      },
      {
        text: `8.16. By confirming a bet, a customer confirms his willful intention to conclude such a transaction.
        On confirmation, the transaction is deemed to be completed and as such, nonreversible, without prejudice
        to any clause in the Terms and Conditions that may provide to the contrary.`,
      },
      {
        text: `8.17. Payouts on bets with fixed odds are calculated by the multiplication of the original stake by the
        predetermined decimal odds. The result of this calculation includes the original stake amount, which is
        returned for all real money bets (i.e. not including bets placed from the free bet sports balance).`,
      },
      {
        text: `8.18. Winnings will be paid into the user’s account after the final result is confirmed. Any later
        amendment to the result, e.g. disciplinary procedures (doping) or intervention by ruling bodies which
        lead to a post-event modification of the result, will not be taken into account for the purpose of
        determining the outcome of a bet. However, ${projectStyledName} reserves the right to set aside any winnings, should
        there be investigations into an event as a result of a suspicion of criminal activities that may have
        affected the result of the event. If any irregularity is confirmed, ${projectStyledName} retains the right to cancel
        any related bets.`,
      },
      {
        text: `8.19. When an event is canceled or abandoned and no final result is declared within 48 hours, all bets
        on the event will be declared void.`,
      },
      {
        text: `8.20. In the case of multiple bets (e.g. doubles, trebles, 4-folds, etc.) placed in the form of a
        combination of multiples through the bet slip (e.g. 3 X Doubles, 2 X Trebles), all bets submitted
        through this combination will be settled once the final event in the combination has been settled. For
        example, should you select three markets in your bet slip and place the maximum combination of three Doubles,
        the three Doubles will not be settled until the final market has been settled.`,
      },
      {
        text: `8.21. ${projectStyledName} reserves the right to lock accounts and to cancel all bets placed from accounts that have
        opened multiple accounts for the purpose of placing bets from different accounts. The stakes on these bets
        will be refunded to the customer but not the winnings.`,
      },
      {
        text: `8.22. The customer may request a higher bet limit. The limits can be increased upon a written
        agreement. Please contact our support team to initiate this process.`,
      },
      {
        text: `8.23. The maximum winnings of ARS 25,000.00 per day a bet is accepted on the basis that it is the
        investment of one customer only and that the possible payout amount is the maximum amount that can be won
        by a customer in one day's betting (regardless of stake).`,
      },
      {
        text: `8.24. Where bets have been placed on different days, the maximum amount that can be won in one day’s
        betting is determined by the day on which the final event on each bet is settled, rather than the day on
        which each individual bet is placed.`,
      },
      {
        text: `8.25. ${projectStyledName} retains the right to reduce the limits on any event at any time.`,
      },
      {
        text: `8.26. ${projectStyledName} is not liable for input or processing errors. ${projectStyledName} reserves the right to correct obvious
        errors in particular regarding the input of betting odds e.g. wrong odds, results, teams etc.`,
      },
      {
        text: `8.27. ${projectStyledName} is not liable for correctness or completeness of information provided.`,
      },
      {
        bottom: `${projectStyledName} is not liable for the accuracy of live scores, statistics and interim results in live
        betting.`,
      },
    ],
  },
  bettingSoftware: {
    label: 'Betting Software Error',
    items: [
      {
        text: `9.1. If betting software errors or malfunction occur, caused by ${projectStyledName} on their own systems or the
        systems ${projectStyledName} makes available to other providers, all bets will be declared void. The customer is entitled
        to a full refund of the betting stake. ${projectStyledName} is not liable for these software malfunctions.`,
      },
      {
        text: `9.2. Every customer who has discovered a malfunction in the gaming and betting software is obliged to
        immediately contact a representative of ${projectStyledName}.`,
      },
      {
        text: `9.3. In the case of misuse or a malfunction caused by a customer, ${projectStyledName} reserves the right to block the
        user’s account, freeze the balance and withhold and/or cancel payments of all outstanding winnings that are
        directly or indirectly a result of the misuse. If the account does not have sufficient coverage for, ${projectStyledName}
        reserves the right to reclaim wrongly paid out winnings. Until the amount is fully paid, the customer is a
        debtor of ${projectStyledName}.`,
      },
      {
        text: `9.4. Furthermore, ${projectStyledName} does not assume responsibility that the information provided, such as result
        notifications by e-mail and other content, are correct, made available and up-to-date. Also, live scores,
        stats and interim results of live betting are provided without guarantee.`,
      },
      {
        text: `9.5. ${projectStyledName} reserves the right to make subsequent corrections or to declare a bet invalid if the bet was
        won due to a technical problem, provided the company can prove this problem by offering technical records.
        The stake will be refunded to the customer’s account.`,
      },
      {
        bottom: `${projectStyledName} reserves the right to make the customer liable for all resulting damages, if he knowingly
        enjoyed benefits that arose from technical and administrative errors.`,
      },
    ],
  },
  privacyPolicy: {
    label: 'Privacy Policy',
    items: [
      {
        text: `10.1. The customer hereby acknowledges and accepts that ${projectStyledName} has the right to collect his personal
        data in order to allow him access and use of the website and to allow him to place bets.`,
      },
      {
        text: `10.2. ${projectStyledName} hereby acknowledges that in collecting the customer’s personal details as stated in the
        previous provision, we are bound by the Data Protection Act. ${projectStyledName} will protect the customer’s personal
        information and respect his privacy in accordance with best business practices and applicable laws.`,
      },
      {
        text: `10.3. ${projectStyledName} will only use the customer’s personal data to allow him to participate in sports betting
        and to carry out operations relevant to his participation in the sports betting.`,
      },
      {
        text: `10.4. The customer’s personal data will not be disclosed to third parties, unless such disclosure is
        necessary for processing his requests in relation to sports betting or unless it is required by law.
        The customer hereby consents to such disclosures.`,
      },
      {
        text: `10.5. The customer has the right to access his personal data held by ${projectStyledName}.`,
      },
      {
        text: `10.6. In order to provide the customer with an efficient service, ${projectStyledName} may require to transfer his
        personal data from one country to another on a world-wide basis. The customer hereby consents to such
        disclosures.`,
      },
      {
        text: `10.7. When processing the customer’s betting account and associated transactions, ${projectStyledName} reserves the
        right to contact credit rating agencies, fraud detection agencies, and anti-money laundering agencies.
        The customer hereby consents to such disclosures.`,
      },
      {
        text: `10.8. In order to make your visit to the Websites more user-friendly, to keep track of visits to the
        Websites and to improve the service, ${projectStyledName} collects a small piece of information sent from your browser,
        called a cookie. You can, if you wish, turn off the collection of cookies (please refer to your browser
        instructions as to how to do this). You must note, however, that turning off cookies may restrict your
        use of the Websites.`,
      },
      {
        text: `10.9. We may also inform you of changes, new services and promotions we think that you may find
        interesting. If you do not wish to receive direct marketing data you may opt out of such service
        (please refer to your browser instructions as to how to do this).`,
      },
    ],
  },
  settingLimit: {
    label: 'Setting up Limits',
    items: [
      {
        text: '11.1. The customer can choose to set up daily, weekly or monthly personal limits.',
      },
      {
        text: `11.2. To change personal limits, the customer has to contact the representative of ${projectStyledName}.`,
      },
      {
        text: 'The following restrictions (limits) may be applied to a user account:',
      },
      {
        text: 'a)Restriction of monetary amounts that may be used during specified period on the account;',
      },
      {
        text: 'b)Restriction of losses that may be made during a certain time on your account;',
      },
      {
        text: 'c)Restriction on the amount that may be bet during a single session on our website.',
      },
    ],
  },
  complaints: {
    label: 'Complaints',
    items: [
      {
        text: `12.1. In case of any complaints, the customers can contact a representative of ${projectStyledName} who has opened
        this account. ${projectStyledName} will use its best efforts to resolve the reported matter promptly.`,
      },
    ],
  },
  softwareProtection: {
    label: 'Software Protection',
    items: [
      {
        text: `13.1. The Website and the computer software are provided "as is”. ${projectStyledName} makes no warranty or
        representation, whether express or implied, in relation to the satisfactory quality, fitness for purpose,
        completeness or accuracy of the website or the computer software.`,
      },
      {
        text: `13.2. ${projectStyledName} shall not be liable for computer malfunctions, failure of telecommunications service or
        Internet connections nor attempts by the customer to participate in placing bets by methods, means or ways not
        intended by ${projectStyledName}.`,
      },
      {
        text: `13.3. ${projectStyledName} cannot guarantee that the Service will never be faulty but will correct reported faults as
        soon as we reasonably can. If a fault occurs, the customer should report it by email to the Customer
        Support.`,
      },
      {
        text: `13.4. Although ${projectStyledName} shall take all reasonable measures to ensure that the computer software and
        files are free from computer viruses, ${projectStyledName} cannot and does not guarantee that the computer software and
        files are free of such problems. It is the customer’s responsibility to protect his systems and have in place
        the ability to reinstall any data or programs lost due to a virus.`,
      },
      {
        text: `13.5. ${projectStyledName} may temporarily suspend the whole or any part of the website for any reason at its sole
        discretion. ${projectStyledName} may, but shall not be obliged to, give the customer as much notice as is reasonably
        practicable in case of such suspension. ${projectStyledName} will restore the website, as soon as is reasonably practicable,
        after temporary suspension.`,
      },
      {
        text: `13.6. In the event of a website system malfunction, all bets are void. In particular, this includes
        betting events and bets that have been removed from the system due to a failure or had to be removed because
        of the failure-related errors. This also applies retroactively upon completion of the bet or after completion
        of the betting event, if the system-related fault and their failure effects have become known until then.`,
      },
    ],
  },
  limitationLiability: {
    label: 'Limitation of Liability',
    items: [
      {
        text: `14.1. The customer participates in sports betting at his own risk. The website and offer are provided
        without any warranty whatsoever, whether express or implied.`,
      },
      {
        text: `14.2. Without prejudice to the generality of the preceding provision, ${projectStyledName}, its directors, employees,
        partners, service providers: for their purpose; do not warrant that the software and Website are free from
        errors; do not warrant that the Websites and/or matches will be accessible without interruptions; shall not
        be liable for any loss, costs, expenses or damages, whether direct, indirect, special, consequential,
        incidental or otherwise, arising in relation to the customer’s use of the website or participation in
        betting.`,
      },
      {
        text: `14.3. The customer hereby agrees to fully indemnify and hold harmless ${projectStyledName}, its directors, employees,
        partners, and service providers for any cost, expense, loss, damages, claims and liabilities however caused
        that may arise in relation to his use of the website or participation in the betting.`,
      },
    ],
  },
  breachesPenalties: {
    label: 'Breaches, Penalties and Termination',
    items: [
      {
        text: `15.1. If the customer breaches any provision of these T&C or ${projectStyledName} has a reasonable ground to suspect
        that he has breached them, ${projectStyledName} reserves the right not to open, suspend, close the user account, withhold
        payment of its winnings and apply these funds in account of any damages incurred.`,
      },
      {
        text: `15.2. The customer acknowledges that ${projectStyledName} shall be the final decision maker of whether he has violated
        ${projectStyledName} rules, terms or conditions in a manner that results in his suspension or permanent barring from
        participation in our site.`,
      },
    ],
  },
  severability: {
    label: 'Severability',
    items: [
      {
        text: `16.1. If any provision of these T&C is held to be illegal or unenforceable, such provision shall be
        severed from these T&C and all other provisions shall remain in force unaffected by such severance.`,
      },
    ],
  },
  assignment: {
    label: 'Assignment',
    items: [
      {
        text: `17.1. ${projectStyledName} reserves the right to change these terms and conditions in accordance with the currently
        applicable laws. Therefore, these Terms and Conditions are no claims against ${projectStyledName} from a practice based
        on these amendments.`,
      },
    ],
  },
  problemGambiling: {
    label: 'Problem Gambling',
    items: [
      {
        bottom: `Problem gambling (or ludomania, but usually referred to as "gambling addiction" or "compulsive
        gambling") is an urge to gamble continuously despite harmful negative consequences or a desire to stop.
        Problem gambling is often defined by whether harm is experienced by the gambler or others, rather than by
        the gambler's behavior. Severe problem gambling may be diagnosed as clinical pathological gambling if the
        gambler meets certain criteria. Pathological gambling is a common disorder that is associated with both
        social and family costs.`,
      },
      {
        text: '18.1. Gambling addiction signs and symptoms',
      },
      {
        bottom: `Gambling addiction is sometimes referred to as a "hidden illness" because there are no obvious
        physical signs or symptoms like there are in drug or alcohol addiction. Problem gamblers also typically
        deny or minimize the problem—even to themselves. However, you may have a gambling problem if you
        Feel the need to be secretive about your gambling. You might gamble in secret or lie about how much you
        gamble, feeling others won’t understand or that you will surprise them with a big win.`,
      },
      {
        bottom: `Have trouble controlling your gambling. Once you start gambling, can you walk away? Or are you
        compelled to gamble until you’ve spent your last dollar, upping your bets in a bid to win lost money back?`,
      },
      {
        bottom: `Gamble even when you don’t have the money. You may gamble until you’ve spent your last dollar,
        and then move on to money you don’t have—money to pay bills, credit cards, or things for your children.
        You may feel pushed to borrow, sell, or even steal things for gambling money.`,
      },
      {
        bottom: `Have family and friends worried about you. Denial keeps problem gambling going. If friends and
        family are worried, listen to them carefully. It’s not a sign of weakness to ask for help.`,
      },
      {
        bottom: `Many older gamblers are reluctant to reach out to their adult children if they've gambled away their
        inheritance, but it's never too late to make changes for the better.`,
      },
      {
        text: '18.2. Self-help for gambling problems',
      },
      {
        bottom: `The biggest step to overcoming a gambling addiction is realizing that you have a problem. It takes
        tremendous strength and courage to own up to this, especially if you have lost a lot of money and
        strained or broken relationships along the way.`,
      },
      {
        bottom: `Don’t despair, and don’t try to go it alone. Many others have been in your shoes and have been
        able to break the habit and rebuild their lives. You can, too.`,
      },
      {
        bottom: `Learn to relieve unpleasant feelings in healthier ways. Do you gamble when you’re lonely or bored?
        Or after a stressful day at work or following an argument with your spouse? Gambling may be a way to
        self-soothe unpleasant emotions, unwind, or socialize. But there are healthier and more effective ways of
        managing your moods and relieving boredom, such as exercising, spending time with friends who don’t gamble,
        taking up new hobbies, or practicing relaxation techniques.`,
      },
      {
        bottom: `Strengthen your support network. It’s tough to battle any addiction without support, so reach out
        to friends and family. If your support network is limited, there are ways to make new friends without relying
        on visiting casinos or gambling online. Try reaching out to colleagues at work, joining a sports team or
        book club, enrolling in an education class, or volunteering for a good cause.`,
      },
      {
        bottom: `Join a peer support group. Gamblers Anonymous, for example, is a twelve-step recovery program
        patterned after Alcoholics Anonymous. A key part of the program is finding a sponsor, a former gambler who
        has experience remaining free from addiction and can provide you invaluable guidance and support.`,
      },
    ],
  },
}
